import React, { memo, useMemo } from 'react';
import Image from 'next/image';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@repo/ui/components/ui/avatar';
import { cn } from '@repo/ui/lib/utils';

import { isStaticBrand } from '.';

interface AppNavigatorLogoProps {
  brandId: number;
  brand?: string;
  logoCircleUrl?: string | null;
  size?: 'sm' | 'md' | 'lg';
}

const PureAppNavigatorLogo: React.FC<AppNavigatorLogoProps> = ({
  brandId,
  brand,
  logoCircleUrl,
  size = 'md',
}) => {
  const sizeClasses = {
    sm: 'size-6 cred-md:size-7 cred-lg:size-[30px] cred-xl:size-10',
    md: 'size-6 cred-md:size-7 cred-lg:size-[30px] cred-xl:size-10',
    lg: 'size-8 cred-md:size-9 cred-lg:size-[37px] cred-xl:size-12',
  };

  const brandNameInitial = useMemo(() => {
    if (!brand) return 'VC';
    const firstName = brand.split(' ')[0] ?? '';
    const lastName = brand.split(' ')[1] ?? '';
    return `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`;
  }, [brand]);

  if (isStaticBrand(brandId)) {
    return (
      <div
        className={cn(
          'flex overflow-hidden rounded-full border',
          sizeClasses[size],
        )}
      >
        <Image
          src={logoCircleUrl ?? ''}
          alt="Skim Circle Logo"
          width={100}
          height={100}
          priority
          className="size-full overflow-hidden rounded-full bg-white object-contain"
        />
      </div>
    );
  }

  if (brand) {
    return (
      <div
        className={cn(
          'flex overflow-hidden rounded-full border',
          sizeClasses[size],
        )}
      >
        <Avatar className="size-full overflow-hidden rounded-full object-contain">
          <AvatarImage src={logoCircleUrl || undefined} alt={`${brand} logo`} />
          <AvatarFallback className="text-white bg-black text-xs">
            {brandNameInitial}
          </AvatarFallback>
        </Avatar>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'overflow-hidden rounded-full bg-neutral-1/40',
        sizeClasses[size],
      )}
    />
  );
};

export const AppNavigatorLogo = memo(
  PureAppNavigatorLogo,
  (prevProps, nextProps) => {
    return (
      prevProps.brandId === nextProps.brandId &&
      prevProps.brand === nextProps.brand &&
      prevProps.logoCircleUrl === nextProps.logoCircleUrl &&
      prevProps.size === nextProps.size
    );
  },
);
